import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, DollarSign, Wallet, Clock, TrendingUp } from "lucide-react";

import SEO from "@/components/shared/SEO";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cleanerApi, getCleanerToken, unwrap } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

function monthLabel(key, locale) {
  if (!key) return "";
  const [y, m] = key.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString(locale === "es" ? "es-ES" : "en-US", { month: "long", year: "numeric" });
}

export default function CleanerEarningsPage() {
  const { locale } = useI18n();
  const navigate = useNavigate();
  const L = (en, es) => (locale === "es" ? es : en);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState("");
  const [status, setStatus] = useState("all");

  const load = useCallback(async () => {
    if (!getCleanerToken()) { navigate("/cleaner/login"); return; }
    try {
      const params = new URLSearchParams();
      if (month) params.set("month", month);
      if (status) params.set("status", status);
      const r = await cleanerApi.get(`/cleaner/earnings?${params.toString()}`);
      setData(unwrap(r));
    } catch {
      navigate("/cleaner/login");
    } finally { setLoading(false); }
  }, [month, status, navigate]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const items = data?.items || [];

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="cleaner-earnings-page">
      <SEO title={L("My earnings — Ever Clean Home", "Mis ganancias — Ever Clean Home")} noindex />
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/brand/logo-everclean-wordmark.png" alt="Ever Clean Home" className="h-11 md:h-14 w-auto object-contain" />
          </div>
          <button onClick={() => navigate("/cleaner/dashboard")} className="flex items-center gap-2 text-sm font-medium text-[#4B5563] hover:text-[#0EA5A4]" data-testid="earnings-back-link">
            <ArrowLeft className="w-4 h-4" /> {L("Back to dashboard", "Volver al panel")}
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-10 space-y-6">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">{L("My earnings", "Mis ganancias")}</h1>
          <p className="text-[#4B5563] text-sm mt-1.5">
            {L("Your share of every job you've completed. Payouts land in your bank 1–2 business days after we send them.",
               "Tu parte de cada trabajo completado. Los pagos llegan a tu banco 1–2 días hábiles después de enviarlos.")}
          </p>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" /></div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="earnings-summary">
              <SummaryCard icon={TrendingUp} label={L("This month", "Este mes")} value={money(summary.this_month)}
                accent="bg-[#0EA5A4]/10 text-[#0F766E]" testid="earnings-this-month" />
              <SummaryCard icon={Clock} label={L("Pending", "Pendiente")} value={money(summary.pending)}
                accent="bg-amber-100 text-amber-700" testid="earnings-pending" />
              <SummaryCard icon={Wallet} label={L("Lifetime", "Total histórico")} value={money(summary.lifetime)}
                accent="bg-emerald-100 text-emerald-700" testid="earnings-lifetime" />
            </div>

            <div className="flex flex-wrap gap-3">
              <select value={month} onChange={(e) => setMonth(e.target.value)}
                className="h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="earnings-month-filter">
                <option value="">{L("All months", "Todos los meses")}</option>
                {(data?.months || []).map((m) => <option key={m} value={m}>{monthLabel(m, locale)}</option>)}
              </select>
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="earnings-status-filter">
                <option value="all">{L("All", "Todos")}</option>
                <option value="paid_out">{L("Paid out", "Pagado")}</option>
                <option value="pending">{L("Pending", "Pendiente")}</option>
              </select>
            </div>

            <Card className="rounded-2xl border border-[#E5E7EB] overflow-hidden">
              {items.length === 0 ? (
                <div className="py-16 text-center text-[#9CA3AF] text-sm" data-testid="earnings-empty">
                  <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  {L("No completed jobs yet. Your earnings will show up here after your first cleaning.",
                     "Todavía no hay trabajos completados. Tus ganancias van a aparecer aquí después de tu primera limpieza.")}
                </div>
              ) : (
                <>
                  {/* Mobile: stacked cards — cleaners work from their phones */}
                  <ul className="sm:hidden divide-y divide-[#F1F1F0]" data-testid="earnings-list-mobile">
                    {items.map((e) => (
                      <li key={e.booking_id} className="p-4" data-testid={`earnings-card-${e.booking_number}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-medium truncate">{e.customer_first_name || "—"}</div>
                            <div className="text-xs text-[#4B5563] mt-0.5">{e.date || "—"}</div>
                            <div className="text-xs text-[#9CA3AF]">{e.area || "—"}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-mono font-bold text-emerald-700">{money(e.amount)}</div>
                            <div className="mt-1.5">
                              {e.status === "paid_out" ? (
                                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium text-[10px]">
                                  {L("Paid out", "Pagado")}
                                </Badge>
                              ) : (
                                <Badge className="bg-amber-50 text-amber-700 border border-amber-200 font-medium text-[10px]">
                                  {L("Pending", "Pendiente")}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-sm" data-testid="earnings-table">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E7EB]">
                      <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563]">
                        <th className="px-4 py-3 font-semibold">{L("Date", "Fecha")}</th>
                        <th className="px-4 py-3 font-semibold">{L("Customer", "Cliente")}</th>
                        <th className="px-4 py-3 font-semibold">{L("Area", "Zona")}</th>
                        <th className="px-4 py-3 font-semibold text-right">{L("Your share", "Tu parte")}</th>
                        <th className="px-4 py-3 font-semibold">{L("Status", "Estado")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F1F1F0]">
                      {items.map((e) => (
                        <tr key={e.booking_id} className="hover:bg-[#FAFAF9]" data-testid={`earnings-row-${e.booking_number}`}>
                          <td className="px-4 py-3 whitespace-nowrap">{e.date || "—"}</td>
                          <td className="px-4 py-3 font-medium">{e.customer_first_name || "—"}</td>
                          <td className="px-4 py-3 text-[#4B5563]">{e.area || "—"}</td>
                          <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-700" data-testid={`earnings-amount-${e.booking_number}`}>
                            {money(e.amount)}
                          </td>
                          <td className="px-4 py-3">
                            {e.status === "paid_out" ? (
                              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium" data-testid={`earnings-status-${e.booking_number}`}>
                                {L("Paid out", "Pagado")}
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-50 text-amber-700 border border-amber-200 font-medium" data-testid={`earnings-status-${e.booking_number}`}>
                                {L("Pending", "Pendiente")}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </>
              )}
            </Card>

            {items.length > 0 && (
              <div className="flex items-center justify-between text-sm px-1">
                <span className="text-[#4B5563]">{items.length} {L("job(s) shown", "trabajo(s) mostrados")}</span>
                <span className="font-mono font-bold" data-testid="earnings-filtered-total">{money(data?.filtered_total)}</span>
              </div>
            )}

            <Button onClick={() => navigate("/cleaner/dashboard")} variant="outline" className="rounded-xl" data-testid="earnings-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> {L("Back to dashboard", "Volver al panel")}
            </Button>
          </>
        )}
      </main>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card className="rounded-2xl p-5 border border-[#E5E7EB]">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold">{label}</div>
          <div className="font-mono text-2xl font-bold mt-2" data-testid={testid}>{value}</div>
        </div>
        <span className={`w-10 h-10 rounded-xl grid place-items-center ${accent}`}><Icon className="w-5 h-5" /></span>
      </div>
    </Card>
  );
}
